import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2RoundedIcon from '@mui/icons-material/StickyNote2Rounded';
import {
  Done as DoneIcon,
  Error as ErrorIcon,
  AutoMode as AutoModeIcon,
} from '@mui/icons-material';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { amber, deepPurple, lime, red, green, grey } from '@mui/material/colors';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import axios from '../../api/axios';

const ViewPlanInfo = ({ plan, onClose }) => {
  const GET_ORG = "/org/";
  const UPLOAD_PDF_FILE = "/uploadPDFfile/";
  const [orgSettings, setOrgSettings] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (plan) {
      console.log("plan ", plan);
      setOpenModal(true);
    }
  }, [plan]);

  const formatDateTooltip = (dateString) => {
    if (!dateString) return '[no date]';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadPdf = async () => {
    const pdfData = plan?.pdfFiles;
    if (!pdfData) return;

    try {
      const response = await axios.post(UPLOAD_PDF_FILE, {
        token: localStorage.getItem('token'),
        downloadPdfFile: true,
        orgId: localStorage.getItem('orgId'),
        pdfId: pdfData.id,
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfData.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');

    axios.post(GET_ORG, {
      token,
      orgSettings: "true"
    })
    .then((res) => {
      const settings = res.data?.message?.settings ?? null;
      setOrgSettings(settings);
    })
    .catch((err) => {
      console.error("Error fetching org settings:", err);
      setOrgSettings(null); // keep it explicit
    });
  }, []);

  const anchorReady = !!plan && (typeof plan.nonstandardAnchor !== 'undefined') && (orgSettings !== null);
  const cadStatus = plan?.fileStatus || 'ready';

  return (
    <Drawer
      anchor="right"
      open={openModal}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 600,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ m: 0, lineHeight: 1.2 }}>Plan Info</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Metadata (static text) */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Last Modified {formatDateTooltip(plan?.planModifiedDateTime)}
        </Typography>

        {/* Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          <Chip
            label={plan?.builderName || '[BuilderName]'}
            sx={{ backgroundColor: '#80DEEA', fontWeight: 500 }}
          />
          {plan?.project && plan?.project !== "No Project Assigned" && (
            <Chip
              label={plan.project}
              sx={{
                backgroundColor: amber[400],
                color: '#000',
                fontWeight: 500,
              }}
            />
          )}
          {plan?.planNumber && (
            <Chip label={plan.planNumber} sx={{ backgroundColor: deepPurple[100], color: '#000', fontWeight: 500 }} />
          )}
          {plan?.elevation && (
            <Chip label={plan.elevation} sx={{ backgroundColor: lime[300], color: '#000', fontWeight: 500 }} />
          )}
          {plan?.lot && <Chip label={plan.lot} sx={{ color: '#000', fontWeight: 500 }} />}
          {plan?.layout && <Chip label={plan.layout} sx={{ color: '#000', fontWeight: 500 }} />}
          {plan?.buildStages?.length > 0 && plan?.buildStages[0]?.buildStageName && (
            <Chip label={plan?.buildStages[0]?.buildStageName} sx={{ color: '#000', fontWeight: 500 }} />
          )}
          {plan?.options?.map((option, index) => (
            <Chip
              key={index}
              label={option.planOptionName || option.name || option}
              sx={{ color: '#000', fontWeight: 500 }}
            />
          ))}
        </Box>

        {/* CAD File */}
        <Stack direction="row" spacing={1} alignItems="center" color={"text.secondary"} sx={{ mb: 1 }}>
          <ArchitectureOutlinedIcon />
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>CAD File</Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            boxSizing: 'border-box',
            minHeight: '80px',
            padding: '16px',
            border: '1px solid lightgray',
            textAlign: "left",
            mb: 3,
          }}
        >
          <Box display="flex" alignItems="center">
            {cadStatus === 'processing' ? (
              <AutoModeIcon
                sx={{
                  mr: 1,
                  color: '#f57c00',
                  animation: 'spin 1.2s linear infinite',
                }}
              />
            ) : cadStatus === 'ready' ? (
              <DoneIcon
                color={'success'}
                sx={{ mr: 1 }}
              />
            ) : (
              <ErrorIcon
                color={'error'}
                sx={{ mr: 1 }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start"
              }}
            >
              <Typography variant="body2">{plan?.fileName || '[FileName.dwg]'}</Typography>
              <Typography variant="caption" color={cadStatus === "error" ? "error" : "text.secondary"}>
                {cadStatus === 'processing'
                  ? 'Processing'
                  : cadStatus === 'error'
                    ? 'Error'
                    : 'Ready'}
                {plan?.lastModifiedCadFileDateTime
                  ? ` • ${new Date(plan.lastModifiedCadFileDateTime).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                  : ""}
                {plan?.uploadedCadFileDateTime
                  ? ` • Uploaded ${new Date(plan.uploadedCadFileDateTime).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}`
                  : ""}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Plan PDF */}
        <Stack direction="row" spacing={1} alignItems="center" color={"text.secondary"} sx={{ mb: 1 }}>
          <DescriptionOutlinedIcon />
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>Plan PDF</Typography>
        </Stack>
        {plan?.pdfFiles ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: '100%',
              boxSizing: 'border-box',
              minHeight: "80px",
              padding: "16px",
              border: "1px solid lightgray",
              mb: 3,
            }}
          >
            <Box display="flex" alignItems="center">
              <PictureAsPdfIcon sx={{ mr: 1, color: "#1976d2" }} />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline", textAlign: "left" }}
                  onClick={handleDownloadPdf}
                >
                  {plan.pdfFiles.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plan.pdfFiles.fileDateTime
                    ? `${new Date(plan.pdfFiles.fileDateTime).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                    : ""}
                  {plan.pdfFiles.uploadedDateAndTime
                    ? ` • Uploaded ${new Date(plan.pdfFiles.uploadedDateAndTime).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}`
                    : ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: '100%',
              boxSizing: 'border-box',
              minHeight: "80px",
              padding: "16px",
              border: "1px solid lightgray",
              mb: 3,
            }}
          >
            <PictureAsPdfIcon sx={{ mr: 1, color: grey[500] }} />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
              No file attached
            </Typography>
          </Box>
        )}

        {/* Anchor Info - show skeleton/placeholder until both plan and orgSettings are ready */}
        {!anchorReady ? (
          <Box display="flex" alignItems="center" sx={{ mb: 2 }} aria-busy="true">
            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
            <Skeleton variant="text" width="60%" />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            {plan.nonstandardAnchor ? (
              <CenterFocusWeakIcon sx={{ color: red[500], mr: 1 }} />
            ) : (
              <CenterFocusStrongIcon sx={{ color: green[500], mr: 1 }} />
            )}

            <Typography variant="body2">
              <strong>Anchor Placement:</strong>{' '}
              {plan.nonstandardAnchor
                ? plan.anchorNote || 'No anchor note provided.'
                : orgSettings?.standard_anchor_placement || 'Standard placement not defined'}
            </Typography>
          </Box>
        )}

        {/* Plan Notes */}
        {plan?.planNote && (
          <Box display="flex" alignItems="center">
            <StickyNote2RoundedIcon sx={{ mr: 1, color: grey[600] }} />
            <Typography variant="body2">
              <strong>Plan Notes:</strong> {plan.planNote}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: '#1976D2',
            color: '#FFFFFF',
            borderRadius: '6px',
            margin: '24px',
            letterSpacing: '0.4px',
            lineHeight: '24px',
            px: 3,
            py: 1,
            boxShadow: 1,
            '&:hover': {
              backgroundColor: '#1565C0',
            },
          }}
        >
          CLOSE
        </Button>
      </Box>
    </Drawer>
  );
};

export default ViewPlanInfo;